import type { ExecutionStep } from '../ui/chat';
import {
  getAgentMeshSourceDefinition,
  type AgentMeshSourceType,
} from '../../lib/agent-mesh-sources';
import type { RuntimeProviderAuthRequired } from '../../lib/retrieval-runtime-auth';

type ProviderAuthChunk = {
  metadata?: Record<string, any>;
};

const BASE_STEP_IDS = {
  submitted: 'submitted',
  planning: 'planning',
  tools: 'tools',
  generating: 'generating',
} as const;

function toTitleCase(raw: string): string {
  return raw
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getFriendlySourceName(raw?: string): string {
  const value = String(raw || '').trim();
  const lower = value.toLowerCase();

  if (!lower) return 'connected data';

  const definition = getAgentMeshSourceDefinition(lower as AgentMeshSourceType);
  if (definition) {
    return definition.label;
  }

  if (lower.includes('hubspot')) return 'HubSpot CRM';
  if (lower.includes('bigquery')) return 'BigQuery';
  if (lower.includes('snowflake')) return 'Snowflake';
  if (lower.includes('fathom')) return 'meeting notes';
  if (lower.includes('granola')) return 'Granola notes';
  if (lower.includes('salesforce')) return 'Salesforce CRM';

  return toTitleCase(value);
}

function extractQueryFromSearchRequestJson(raw: unknown): string | undefined {
  if (typeof raw !== 'string' || !raw.trim()) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return undefined;
    }
    const queryCandidateKeys = ['query', 'q', 'search', 'term'];
    for (const key of queryCandidateKeys) {
      const value = (parsed as Record<string, unknown>)[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    const filterGroups = (parsed as Record<string, unknown>).filterGroups;
    if (Array.isArray(filterGroups)) {
      for (const group of filterGroups) {
        if (!group || typeof group !== 'object') continue;
        const filters = (group as Record<string, unknown>).filters;
        if (!Array.isArray(filters)) continue;
        for (const filter of filters) {
          if (!filter || typeof filter !== 'object') continue;
          const value = (filter as Record<string, unknown>).value;
          if (typeof value === 'string' && value.trim()) {
            return value.trim();
          }
        }
      }
    }
  } catch {
    return undefined;
  }
  return undefined;
}

function getToolCallHints(
  params?: Record<string, unknown>,
  rawDescription?: string,
): { query?: string; recordingId?: string } {
  let query: string | undefined;
  let recordingId: string | undefined;

  if (params && typeof params === 'object') {
    if (typeof params.search_query === 'string' && params.search_query.trim()) {
      query = params.search_query.trim();
    }
    if (!query) {
      query = extractQueryFromSearchRequestJson(params.search_request_json);
    }
    if (params.recording_id !== undefined && params.recording_id !== null) {
      const candidate = String(params.recording_id).trim();
      if (candidate) {
        recordingId = candidate;
      }
    }
    const nestedArguments = params.arguments;
    if (nestedArguments && typeof nestedArguments === 'object') {
      for (const key of ['query', 'search_query', 'search', 'term']) {
        const value = (nestedArguments as Record<string, unknown>)[key];
        if (typeof value === 'string' && value.trim()) {
          query = value.trim();
          break;
        }
      }
      for (const key of ['meeting_id', 'recording_id', 'id']) {
        const value = (nestedArguments as Record<string, unknown>)[key];
        if (value !== undefined && value !== null) {
          const candidate = String(value).trim();
          if (candidate) {
            recordingId = candidate;
            break;
          }
        }
      }
    }
  }

  if (typeof rawDescription === 'string' && rawDescription.trim()) {
    if (!query) {
      const queryMatch = rawDescription.match(/(?:^|\|)\s*query=(.+?)(?:\s*\|\s*|$)/i);
      if (queryMatch?.[1]) {
        query = queryMatch[1].trim();
      }
    }
    if (!recordingId) {
      const recordingMatch = rawDescription.match(/recording_id=(\d+)/i);
      if (recordingMatch?.[1]) {
        recordingId = recordingMatch[1].trim();
      }
    }
  }

  if (query && query.length > 80) {
    query = `${query.slice(0, 77)}...`;
  }

  return { query, recordingId };
}

function getToolAction(action?: string | null, params?: Record<string, unknown>): string | undefined {
  if (typeof action === 'string' && action.trim()) {
    return action.trim();
  }
  if (params && typeof params.tool_name === 'string' && params.tool_name.trim()) {
    return params.tool_name.trim();
  }
  return undefined;
}

function getFriendlyToolLabel(
  tool?: string,
  action?: string | null,
  sourceHint?: string,
  params?: Record<string, unknown>,
  rawDescription?: string,
): string {
  const normalizedTool = String(tool || '').toLowerCase();
  const definition =
    getAgentMeshSourceDefinition(String(sourceHint || '').toLowerCase() as AgentMeshSourceType) ||
    getAgentMeshSourceDefinition(normalizedTool as AgentMeshSourceType);
  const source = getFriendlySourceName(sourceHint || tool);
  const normalizedAction = String(getToolAction(action, params) || '').toLowerCase();
  const hints = getToolCallHints(params, rawDescription);

  if (definition && definition.id === 'fathom') {
    if (normalizedAction === 'list_meetings') {
      return hints.query ? `Finding meetings about "${hints.query}"` : 'Finding relevant meetings';
    }
    if (normalizedAction === 'get_summary') {
      return hints.recordingId
        ? `Reviewing highlights for meeting #${hints.recordingId}`
        : 'Reviewing meeting highlights';
    }
    if (normalizedAction === 'get_transcript') return 'Reviewing meeting transcript';
    return 'Checking meeting notes';
  }

  if (definition && definition.id === 'hubspot') {
    return hints.query ? `Checking CRM records for "${hints.query}"` : 'Checking HubSpot records';
  }

  if (definition && definition.id === 'granola') {
    if (normalizedAction === 'list_meetings') {
      return hints.query
        ? `Finding Granola meetings about "${hints.query}"`
        : 'Finding relevant Granola meetings';
    }
    if (normalizedAction === 'get_meeting_transcript') {
      return 'Reviewing meeting transcript';
    }
    if (normalizedAction === 'get_meetings' || normalizedAction === 'query_granola_meetings') {
      return hints.query
        ? `Searching meeting notes for "${hints.query}"`
        : 'Searching Granola notes';
    }
    return 'Checking Granola notes';
  }

  return `Checking ${source}`;
}

function getFriendlyToolDescription(
  tool?: string,
  action?: string | null,
  status: 'active' | 'completed' | 'failed' = 'active',
  params?: Record<string, unknown>,
  rawDescription?: string,
): string {
  const normalizedAction = String(getToolAction(action, params) || '').toLowerCase();
  const normalizedTool = String(tool || '').toLowerCase();
  const definition = getAgentMeshSourceDefinition(normalizedTool as AgentMeshSourceType);
  const hints = getToolCallHints(params, rawDescription);

  if (status === 'failed') {
    return hints.query
      ? `Could not fully check "${hints.query}". Continuing with available context.`
      : 'This source could not be read fully. I continued with available context.';
  }

  if (definition && definition.id === 'fathom') {
    if (normalizedAction === 'list_meetings') {
      return status === 'completed' ? 'Meeting notes checked.' : 'Scanning meeting notes for relevant conversations.';
    }
    if (normalizedAction === 'get_summary') {
      return status === 'completed' ? 'Meeting highlights captured.' : 'Reading key highlights from the selected meeting.';
    }
    if (normalizedAction === 'get_transcript') {
      return status === 'completed' ? 'Transcript details captured.' : 'Reading transcript details for exact context.';
    }
  }

  if (definition && definition.id === 'hubspot') {
    return hints.query
      ? status === 'completed'
        ? `CRM context captured for "${hints.query}".`
        : `Looking through CRM records for "${hints.query}".`
      : status === 'completed'
        ? 'CRM context captured.'
        : 'Looking through CRM data for relevant context.';
  }

  if (definition && definition.id === 'granola') {
    return status === 'completed'
      ? 'Meeting-note context captured.'
      : 'Checking Granola meeting notes.';
  }

  return status === 'completed' ? 'Source check complete.' : 'Checking connected source data.';
}

export function getFriendlyLiveProgressLabel(
  eventType: string,
  metadata: Record<string, unknown>,
): string {
  switch (eventType) {
    case 'connected':
    case 'heartbeat':
    case 'submitted':
    case 'run_started':
      return 'Understanding your question';
    case 'planning_started':
      return 'Figuring out where to look';
    case 'planning_completed':
      return 'Plan ready, starting data checks';
    case 'execution_started':
      return 'Gathering details from connected sources';
    case 'tool_started': {
      const params = metadata.params && typeof metadata.params === 'object'
        ? metadata.params as Record<string, unknown>
        : {};
      return getFriendlyToolLabel(
        String(metadata.tool || ''),
        getToolAction(undefined, params),
        String(metadata.source || ''),
        params,
      );
    }
    case 'tool_completed':
      return 'Captured relevant details';
    case 'tool_failed':
      return 'One source had an issue, continuing with others';
    case 'evaluation_started':
      return 'Checking if the collected context is enough';
    case 'evaluation_completed':
      return metadata.done ? 'Great, we have enough context' : 'Getting a bit more context';
    case 'synthesis_started':
      return 'Putting the answer together';
    case 'synthesis_completed':
      return 'Finalizing the response';
    case 'followups_started':
      return 'Preparing next-question suggestions';
    case 'followups_completed':
      return 'Answer is ready';
    case 'completed':
      return 'Done';
    case 'failed':
      return 'Could not complete this request';
    default:
      return 'Working on your answer';
  }
}

export function extractProviderAuthRequiredFromMessage(
  metadata?: Record<string, any>,
  chunks?: ProviderAuthChunk[],
): RuntimeProviderAuthRequired | null {
  const candidates = [
    metadata?.auth_required,
    metadata?.provider_auth_required,
    ...(chunks || []).flatMap((chunk) => [
      chunk.metadata?.auth_required,
      chunk.metadata?.provider_auth_required,
    ]),
  ];

  const auth = candidates.find((candidate) => candidate && typeof candidate === 'object');
  if (!auth) return null;

  const statusCode = String(auth.code || '').trim().toLowerCase();
  const isProviderAuth =
    auth.type === 'provider_auth_required' ||
    statusCode === 'auth_required' ||
    statusCode === 'auth_expired' ||
    Boolean(auth.authorize_url);

  return isProviderAuth ? (auth as RuntimeProviderAuthRequired) : null;
}

export function createInitialLiveSteps(): ExecutionStep[] {
  return [
    {
      id: BASE_STEP_IDS.submitted,
      label: 'Understanding your question',
      status: 'active',
    },
    {
      id: BASE_STEP_IDS.planning,
      label: 'Finding where the answer is',
      status: 'pending',
      description: 'Choosing the best sources to check first.',
    },
    {
      id: BASE_STEP_IDS.tools,
      label: 'Gathering details from your data',
      status: 'pending',
      description: 'Reading connected records and notes.',
    },
    {
      id: BASE_STEP_IDS.generating,
      label: 'Writing your answer',
      status: 'pending',
      description: 'Combining findings into a clear response.',
    },
  ];
}

function updateStepById(
  steps: ExecutionStep[],
  stepId: string,
  patch: Partial<ExecutionStep>,
): ExecutionStep[] {
  return steps.map((step) => (step.id === stepId ? { ...step, ...patch } : step));
}

function upsertStep(
  steps: ExecutionStep[],
  nextStep: ExecutionStep,
  insertBeforeStepId?: string,
): ExecutionStep[] {
  const existingIndex = steps.findIndex((step) => step.id === nextStep.id);
  if (existingIndex === -1) {
    if (insertBeforeStepId) {
      const insertIndex = steps.findIndex((step) => step.id === insertBeforeStepId);
      if (insertIndex >= 0) {
        return [
          ...steps.slice(0, insertIndex),
          nextStep,
          ...steps.slice(insertIndex),
        ];
      }
    }
    return [...steps, nextStep];
  }
  const updated = [...steps];
  updated[existingIndex] = { ...updated[existingIndex], ...nextStep };
  return updated;
}

function markPipelineCompleted(steps: ExecutionStep[]): ExecutionStep[] {
  return steps.map((step) => {
    if (
      step.id === BASE_STEP_IDS.submitted ||
      step.id === BASE_STEP_IDS.planning ||
      step.id === BASE_STEP_IDS.tools ||
      step.id === BASE_STEP_IDS.generating
    ) {
      return {
        ...step,
        status: step.status === 'failed' ? 'failed' : 'completed',
      };
    }
    return step.status === 'active' ? { ...step, status: 'completed' } : step;
  });
}

function getToolStepId(metadata?: Record<string, unknown>): string {
  const tool = String(metadata?.tool || 'tool');
  const phase = String(metadata?.phase || 'initial');
  const iteration = Number.isFinite(Number(metadata?.iteration)) ? Number(metadata?.iteration) : 0;
  const stepIndex = Number.isFinite(Number(metadata?.step_index)) ? Number(metadata?.step_index) : 0;
  return `tool-${tool}-${phase}-${iteration}-${stepIndex}`;
}

function isKnowledgeBaseProgressTool(metadata?: Record<string, unknown>): boolean {
  const tool = String(metadata?.tool || '').toLowerCase();
  const source = String(metadata?.source || '').toLowerCase();
  return tool === 'knowledge_base' || source === 'knowledge_base';
}

function completeActiveToolSteps(
  steps: ExecutionStep[],
  exceptStepId?: string,
): ExecutionStep[] {
  return steps.map((step) => {
    if (
      step.id !== exceptStepId &&
      step.id.startsWith('tool-') &&
      step.status === 'active'
    ) {
      return { ...step, status: 'completed' };
    }
    return step;
  });
}

function buildToolExecutionStep(
  metadata: Record<string, unknown>,
  status: 'active' | 'completed' | 'failed',
): ExecutionStep {
  const params = metadata.params && typeof metadata.params === 'object'
    ? metadata.params as Record<string, unknown>
    : {};
  return {
    id: getToolStepId(metadata),
    label: getFriendlyToolLabel(
      String(metadata.tool || ''),
      getToolAction(undefined, params),
      String(metadata.source || ''),
      params,
      typeof metadata.description === 'string' ? metadata.description : undefined,
    ),
    status,
    description: getFriendlyToolDescription(
      String(metadata.tool || ''),
      getToolAction(undefined, params),
      status,
      params,
      typeof metadata.description === 'string' ? metadata.description : undefined,
    ),
  };
}

export function applyAgentMeshLiveEventToSteps(
  steps: ExecutionStep[],
  eventType: string,
  metadata: Record<string, unknown> = {},
): ExecutionStep[] {
  let next = steps.length > 0 ? [...steps] : createInitialLiveSteps();

  switch (eventType) {
    case 'submitted':
    case 'run_started':
      return createInitialLiveSteps();
    case 'planning_started':
      next = updateStepById(next, BASE_STEP_IDS.submitted, { status: 'completed' });
      next = updateStepById(next, BASE_STEP_IDS.planning, { status: 'active' });
      break;
    case 'planning_completed':
      next = updateStepById(next, BASE_STEP_IDS.submitted, { status: 'completed' });
      next = updateStepById(next, BASE_STEP_IDS.planning, { status: 'completed' });
      next = updateStepById(next, BASE_STEP_IDS.tools, { status: 'pending' });
      break;
    case 'execution_started':
      next = updateStepById(next, BASE_STEP_IDS.submitted, { status: 'completed' });
      next = updateStepById(next, BASE_STEP_IDS.planning, { status: 'completed' });
      next = updateStepById(next, BASE_STEP_IDS.tools, {
        status: 'active',
        description: 'Checking connected records and notes.',
      });
      break;
    case 'tool_started': {
      if (isKnowledgeBaseProgressTool(metadata)) {
        next = updateStepById(next, BASE_STEP_IDS.tools, {
          status: 'active',
          description: 'Checking connected records and notes.',
        });
        break;
      }

      const stepId = getToolStepId(metadata);
      next = completeActiveToolSteps(next, stepId);
      next = updateStepById(next, BASE_STEP_IDS.tools, {
        status: 'completed',
        description: 'Details from your data captured.',
      });
      next = upsertStep(
        next,
        buildToolExecutionStep(metadata, 'active'),
        BASE_STEP_IDS.generating,
      );
      break;
    }
    case 'tool_completed': {
      if (isKnowledgeBaseProgressTool(metadata)) {
        next = updateStepById(next, BASE_STEP_IDS.tools, {
          status: 'completed',
          description: 'Details from your data captured.',
        });
        break;
      }

      next = upsertStep(
        next,
        buildToolExecutionStep(metadata, 'completed'),
        BASE_STEP_IDS.generating,
      );
      break;
    }
    case 'tool_failed': {
      if (isKnowledgeBaseProgressTool(metadata)) {
        next = updateStepById(next, BASE_STEP_IDS.tools, {
          status: 'completed',
          description: 'Document details were unavailable. Continuing with connected source data.',
        });
        break;
      }

      next = upsertStep(
        next,
        buildToolExecutionStep(metadata, 'failed'),
        BASE_STEP_IDS.generating,
      );
      break;
    }
    case 'synthesis_started':
      next = completeActiveToolSteps(next);
      next = updateStepById(next, BASE_STEP_IDS.tools, {
        status: 'completed',
        description: 'Tool calls finished',
      });
      next = updateStepById(next, BASE_STEP_IDS.generating, {
        status: 'active',
        description: 'Combining everything into a clear answer.',
      });
      break;
    case 'synthesis_completed':
      next = updateStepById(next, BASE_STEP_IDS.generating, { status: 'completed' });
      break;
    case 'completed':
      next = markPipelineCompleted(next);
      break;
    case 'failed': {
      const activeStep = next.find((step) => step.status === 'active');
      if (activeStep) {
        next = updateStepById(next, activeStep.id, {
          status: 'failed',
          description: 'Could not complete this step.',
        });
      } else {
        next = upsertStep(next, {
          id: 'agent-mesh-failed',
          label: 'Could not complete request',
          status: 'failed',
          description: 'Please retry or narrow the question.',
        });
      }
      break;
    }
    default:
      break;
  }

  return next;
}

function stripEnclosingMarkdownFence(raw: string): string {
  const trimmed = raw.trim();
  const fenceMatch = trimmed.match(/^```(?:json|markdown|md|text)?\s*\n([\s\S]*?)\n```$/i);
  return fenceMatch?.[1]?.trim() ?? trimmed;
}

function extractAssistantMarkdownFromJson(raw: string): string | null {
  const candidate = stripEnclosingMarkdownFence(raw);
  if (!/^[{["]/.test(candidate.trim())) {
    return null;
  }

  try {
    const parsed = JSON.parse(candidate);
    if (typeof parsed === 'string') {
      return parsed;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }

    for (const key of ['answer', 'raw', 'response', 'content', 'message', 'summary', 'final_answer']) {
      const value = (parsed as Record<string, unknown>)[key];
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }
  } catch {
    return null;
  }

  return null;
}

export function normalizeAssistantMarkdownContent(content: string): string {
  let normalized = stripEnclosingMarkdownFence(content || '');
  const extracted = extractAssistantMarkdownFromJson(normalized);
  if (extracted) {
    normalized = stripEnclosingMarkdownFence(extracted);
  }

  const actualLineBreakCount = (normalized.match(/\n/g) || []).length;
  const escapedLineBreakCount = (normalized.match(/\\n/g) || []).length;
  if (escapedLineBreakCount > 0 && actualLineBreakCount <= 1) {
    normalized = normalized
      .replace(/\\r\\n/g, '\n')
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '  ');
  }

  return normalized.trim();
}

export function stripGfmTableBlocks(content: string): string {
  const lines = content.split('\n');
  const retained: string[] = [];
  const separatorPattern =
    /^\s*\|?\s*:?-{3,}:?\s*(?:\|\s*:?-{3,}:?\s*)+\|?\s*$/;

  for (let index = 0; index < lines.length; index += 1) {
    const header = lines[index];
    const separator = lines[index + 1];
    if (
      header?.includes('|') &&
      separator !== undefined &&
      separatorPattern.test(separator)
    ) {
      index += 2;
      while (index < lines.length && lines[index].includes('|')) {
        index += 1;
      }
      index -= 1;
      continue;
    }
    retained.push(header);
  }

  return retained.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}
